import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000,
});



// Request Interceptor
// Request Interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor with JWT Refresh
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        });

        if (response.data.success && response.data.token) {
          const newToken = response.data.token;
          localStorage.setItem('authToken', newToken);
          api.defaults.headers.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return api(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error.response?.data || error);
  }
);

  export const authAPI = {
  register: (name, email, password, role) => 
    api.post('/auth/register', { name, email, password, role }),
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  logout: () => 
    api.post('/auth/logout'),
  getProfile: () => 
    api.get('/auth/me'),
  refresh: () => 
    api.post('/auth/refresh'),
};

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);


// Account Management
export const getAccount = () => api.get('/account');
export const updateAccount = (data) => api.put('/account', data);
export const changePassword = (data) => api.put('/account/password', data);
export const enable2FA = () => api.post('/account/2fa/enable');
export const disable2FA = (data) => api.post('/account/2fa/disable', data);
export const verify2FA = (data) => api.post('/account/2fa/verify', data);

// Profile
export const uploadProfilePhoto = (formData) => {
  return api.post('/users/profile/photo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Events
export const getEvents = () => api.get('/events');
export const getEvent = (eventId) => api.get(`/events/${eventId}`);
export const createEvent = (data) => api.post('/events', data);
export const updateEvent = (eventId, data) => api.put(`/events/${eventId}`, data);
export const deleteEvent = (eventId) => api.delete(`/events/${eventId}`);
export const uploadEventPhotos = (eventId, formData) => {
  console.log("Uploading photos for event ID:", eventId, formData); // Debugging log  
  return api.post(`/events/${eventId}/photos`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Event Photos Management
export const browseEventPhotos = (eventId) => api.get(`/events/${eventId}/photos/browse`);


export const downloadMultiplePhotos = (data) =>
  api.post('/photos/download-multiple', data, { responseType: 'blob' });

// Photos
export const getMyPhotos = () => api.get('/my-photos');
export const downloadPhoto = (photoId) => {
  return api.get(`/photos/${photoId}/download`, {
    responseType: 'blob'
  });
};
export const fetchPhotoBlob = async (photoId) => {
  const blob = await api.get(`/photos/${photoId}/view`, { responseType: 'blob' });
  return URL.createObjectURL(blob);
};

// Subscription & Pricing
export const getSubscriptionPlans = () => api.get('/subscription/plans');
export const upgradeSubscription = (data) => api.post('/subscription/upgrade', data);

// Analytics
export const getAnalytics = () => api.get('/analytics');

// Get image URL
export const getImageUrl = (filename) => {

  console.log("Image attempt:" + filename);
  if (!filename) return null;
  // Already a full URL
  if (filename.startsWith('http://') || filename.startsWith('https://')) return filename;
  const base = API_URL.replace('/api', '');
  // Strip any leading /storage/ the backend may have already included
  const clean = filename.replace(/^\/?(storage\/)?/, '');

  let file_url = `${base}/storage/${clean}`;
  console.log("Constructed image URL:", filename, file_url); // Debugging log
  return `${base}/storage/${clean}`;


};




// ... your existing code ...

// ============================================================================
// V2 ADDITIONS - Add these to your existing api.js
// ============================================================================

// Social Features (V2)
export const likePhoto = (photoId) => api.post(`/photos/${photoId}/like`);
export const unlikePhoto = (photoId) => api.delete(`/photos/${photoId}/like`);
export const sharePhoto = (photoId, platform) => 
  api.post(`/photos/${photoId}/share`, { platform });

// Slideshow (V2)
export const getGallerySlideshow = (galleryId) => 
  api.get(`/galleries/${galleryId}/slideshow`);

export const trackPhotoView = (photoId) => 
  api.post(`/photos/${photoId}/view`);

// Gallery Access (V2)
export const requestGalleryAccess = (galleryId, data) => 
  api.post(`/galleries/${galleryId}/request-access`, data);

export const getGalleryAccessRequests = (galleryId) => 
  api.get(`/galleries/${galleryId}/access-requests`);

export const approveAccessRequest = (requestId) => 
  api.post(`/access-requests/${requestId}/approve`);

export const denyAccessRequest = (requestId) => 
  api.post(`/access-requests/${requestId}/deny`);

export const getMyAccessRequests = () => 
  api.get('/my-access-requests');

// Photographer Profile (V2)
export const getPhotographerProfile = (photographerId) => 
  api.get(`/photographers/${photographerId}/profile`);

export const updatePhotographerProfile = (data) => 
  api.put('/photographer/profile', data);

export const generateQRCode = () => 
  api.post('/photographer/qr-code');

// Gallery Management (V2)
export const archiveGallery = (galleryId) => 
  api.post(`/galleries/${galleryId}/archive`);

export const restoreGallery = (galleryId) => 
  api.post(`/galleries/${galleryId}/restore`);

// Photo Management (V2)
export const excludePhotoFromMatching = (photoId) => 
  api.post(`/photos/${photoId}/exclude`);

export const bulkExcludePhotos = (photoIds) => 
  api.post('/photos/bulk-exclude', { photo_ids: photoIds });

// Payments (V2)
export const createCheckoutSession = (plan) => 
  api.post('/checkout', { plan });

export const getPaymentMethods = () => 
  api.get('/payment-methods');

export const addPaymentMethod = (data) => 
  api.post('/payment-methods', data);

export const deletePaymentMethod = (methodId) => 
  api.delete(`/payment-methods/${methodId}`);

// Notifications (V2)
export const getNotifications = () => 
  api.get('/notifications');

export const markNotificationRead = (notificationId) => 
  api.put(`/notifications/${notificationId}/read`);

export const markAllNotificationsRead = () => 
  api.post('/notifications/mark-all-read');

// Admin (V2)
export const getAdminUsers = () => 
  api.get('/admin/users');

export const updateAdminUser = (userId, data) => 
  api.put(`/admin/users/${userId}`, data);

export const suspendUser = (userId) => 
  api.post(`/admin/users/${userId}/suspend`);

export const getAdminAnalytics = () => 
  api.get('/admin/analytics');

export const getActivityLogs = () => 
  api.get('/admin/activity-logs');

// Contact & Marketing (V2)
export const submitContactForm = (data) => 
  api.post('/contact', data);

export const getDemoSlides = () => 
  api.get('/demo/slides');

export const getTestimonials = () => 
  api.get('/testimonials');




// Photo paiment api
export const purchasePhoto = (photoId) => 
  api.post(`/photos/${photoId}/purchase`);

export const checkPhotoAccess = (photoId) => 
  api.get(`/photos/${photoId}/check-access`);

export const getPublicEvents = (params) => 
  api.get('/events/public', { params });

export const setPhotoPrice = (photoId, price) =>
  api.put(`/photos/${photoId}/price`, { price });

export const updatePhoto = (photoId, data) =>
  api.put(`/photos/${photoId}`, data);

export const deletePhoto = (photoId) =>
  api.delete(`/photos/${photoId}`);

export const getMyPurchases = () => 
  api.get('/my-purchases');

export const getPhotographerEarnings = () => 
  api.get('/photographer/earnings');


export default api;
